import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse } from '../request.types';
import {
    GetUserPayload,
    User,
    UpdateUserPayload,
    GetBrandsUsersListPayload,
    UserRoles,
    GetBrandsUsersListResponse,
    CreateManagerPayload,
    UpdatePasswordPayload,
    GetRestaurantUsersResponse,
    GetRestaurantUsersPayload,
    GetRestaurantUsersListPayload,
    GetRestaurantUsersListResponse,
    GetUserRestaurantsResponse,
    GetUserRestaurantsPayload,
    UpdateUserRestaurantsPayload,
    ValidateActivationTokenPayload,
    ValidateActivationTokenResponse,
    ActivateAccountPayload,
    RenewActivationTokenPayload,
} from './user.type';

const userApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        getUser: build.query<User, ApiRequest<GetUserPayload>>({
            query: ({ userId }) => ({
                url: `/users/${userId}`,
            }),
            transformResponse: (response: ApiResponse<User>) => response.data,
            providesTags: (user) => [{ type: ApiTagTypes.User, id: user?.userId }],
        }),
        updateUser: build.mutation<User, ApiRequest<UpdateUserPayload>>({
            query: (user) => ({
                url: `/users/${user.userId}`,
                body: user,
                method: 'PUT',
            }),
            transformResponse: (response: ApiResponse<User>) => response.data,
            invalidatesTags: (user) => [{ type: ApiTagTypes.User, id: user?.userId }],
        }),
        getBrandUsersList: build.query<GetBrandsUsersListResponse, ApiRequest<GetBrandsUsersListPayload>>({
            query: ({ brandId, search, orderByLastConnection, role }) => ({
                url: `/users`,
                params: {
                    brandId,
                    role: role ?? UserRoles.brand,
                    search,
                    orderByLastConnection,
                },
            }),
            transformResponse: (response: ApiResponse<GetBrandsUsersListResponse>) => response.data,
            providesTags: [ApiTagTypes.BrandUsers],
        }),
        getRestaurantKioskUsers: build.query<GetRestaurantUsersResponse, ApiRequest<GetRestaurantUsersPayload>>({
            query: ({ brandId, restaurantId }) => ({
                url: `/users`,
                params: { brandId, restaurantsId: [restaurantId], role: UserRoles.kiosk },
            }),
            transformResponse: (response: ApiResponse<GetRestaurantUsersResponse>) => response.data,
        }),
        getRestaurantUsersList: build.query<GetRestaurantUsersListResponse, ApiRequest<GetRestaurantUsersListPayload>>({
            query: ({ brandId, restaurantId, search, orderByLastConnection }) => ({
                url: `/users`,
                params: {
                    brandId,
                    restaurantsId: [restaurantId],
                    role: UserRoles.restaurant,
                    search,
                    orderByLastConnection,
                },
            }),
            transformResponse: (response: ApiResponse<GetRestaurantUsersListResponse>) => response.data,
            providesTags: [ApiTagTypes.BrandUsers],
        }),
        getUserRestaurants: build.query<GetUserRestaurantsResponse, ApiRequest<GetUserRestaurantsPayload>>({
            query: ({ userId }) => ({
                url: `/users/${userId}/restaurants`,
            }),
            transformResponse: (response: ApiResponse<GetUserRestaurantsResponse>) => response.data,
        }),
        updateUserRestaurants: build.mutation<User, ApiRequest<UpdateUserRestaurantsPayload>>({
            query: ({ userId, restaurantIds }) => ({
                url: `/users/${userId}/restaurants/bulk`,
                body: { restaurantIds },
                method: 'PUT',
            }),
        }),
        createManager: build.mutation<User, ApiRequest<CreateManagerPayload>>({
            query: (body) => ({
                url: `/users/manager`,
                body,
                method: 'POST',
            }),
            invalidatesTags: [ApiTagTypes.BrandUsers],
            transformResponse: (response: ApiResponse<User>) => response.data,
        }),
        updatePassword: build.mutation<User, ApiRequest<UpdatePasswordPayload>>({
            query: ({ userId, oldPassword, newPassword, role, token }) => ({
                url: `/users/${userId}/password`,
                body: { oldPassword, newPassword, role, userId },
                method: 'PUT',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
        }),
        validateActivationToken: build.query<ValidateActivationTokenResponse, ValidateActivationTokenPayload>({
            query: ({ token }) => ({
                url: `users/validate-activation-token`,
                method: 'GET',
                params: { token },
            }),
            transformResponse: (response: ApiResponse<ValidateActivationTokenResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        activateAccount: build.mutation<ApiResponse<Record<string, never>>, ActivateAccountPayload>({
            query: ({ token, password, confirmPassword }) => ({
                url: `users/activate-account`,
                method: 'POST',
                body: {
                    token,
                    password,
                    confirmPassword,
                },
            }),
            extraOptions: { maxRetries: 3 },
        }),
        renewActivationToken: build.mutation<void, RenewActivationTokenPayload>({
            query: ({ token }) => ({
                url: `users/renew-activation-token`,
                method: 'GET',
                params: { token },
            }),
            extraOptions: { maxRetries: 3 },
        }),
    }),
});

export const {
    useGetUserQuery,
    useUpdateUserMutation,
    useLazyGetBrandUsersListQuery,
    useCreateManagerMutation,
    useGetRestaurantKioskUsersQuery,
    useLazyGetRestaurantUsersListQuery,
    useUpdatePasswordMutation,
    useGetUserRestaurantsQuery,
    useUpdateUserRestaurantsMutation,
    useValidateActivationTokenQuery,
    useActivateAccountMutation,
    useRenewActivationTokenMutation,
} = userApi;
